import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as BuyIntentionModels from 'model/BuyIntention';
import * as BuyIntentionCalls from './apiCalls';

export type buyIntentionState = {
  showIntentionModal: boolean;
  currentStep: number;
  pagination: BuyIntentionModels.DefaultPagination;
  reloadPage: boolean;
  customer: BuyIntentionModels.Customer | undefined;
  intentionCustomer: BuyIntentionModels.Customer | undefined;
  productList: any;
  producerList: any;
  buyIntentionEdit: any | undefined;
  overviewEdit: any | undefined;
  offersList: any | undefined;
  subTotalPriceList: BuyIntentionModels.SubTotalPrice[];
  submitOffers: boolean;
  idsProductsSelected: string[];
  idsProductsAccepted: string[];
  deliveryDateForOffers: string | undefined;
  comercialDateForOffers: string | undefined;
  deliveryDateForFinish: string | undefined;
  comercialDateForFinish: string | undefined;
  idSelectedNow: string | undefined;
  isLoading: boolean;
  showModalOffers: boolean;
  idProductAdded: string | undefined;
  totalPriceOffers: BuyIntentionModels.SubTotalPrice[];
  totalPriceOffersAux: BuyIntentionModels.SubTotalPrice[];
  totalPriceOffersOverview: BuyIntentionModels.SubTotalPrice[];
  producers: BuyIntentionModels.Producers[] | undefined;
  reloadOffers: boolean;
  subPriceAdded: number;
  isProducerSearch: boolean;
};

export const initialState: buyIntentionState = {
  showIntentionModal: false,
  currentStep: 0,
  pagination: { PageSize: 10 } as BuyIntentionModels.DefaultPagination,
  reloadPage: false,
  customer: undefined,
  intentionCustomer: undefined,
  productList: undefined,
  producerList: undefined,
  buyIntentionEdit: [],
  overviewEdit: [],
  offersList: [],
  subTotalPriceList: [],
  submitOffers: false,
  idsProductsSelected: [],
  idsProductsAccepted: [],
  deliveryDateForOffers: undefined,
  comercialDateForOffers: undefined,
  deliveryDateForFinish: undefined,
  comercialDateForFinish: undefined,
  idSelectedNow: undefined,
  isLoading: false,
  showModalOffers: false,
  idProductAdded: undefined,
  totalPriceOffers: [],
  totalPriceOffersAux: [],
  totalPriceOffersOverview: [],
  producers: undefined,
  reloadOffers: false,
  subPriceAdded: 0,
  isProducerSearch: false
};

const buyIntentionSlice = createSlice({
  name: 'buyIntention',
  initialState,
  reducers: {
    setShowIntentionModal(state, action: PayloadAction<boolean>) {
      state.showIntentionModal = action.payload;
      if (action.payload) {
        state.intentionCustomer = state.customer;
      }
    },
    setIntentionCustomer(
      state,
      action: PayloadAction<BuyIntentionModels.Customer | undefined>,
    ) {
      state.intentionCustomer = action.payload;
    },
    setClearAssembledOrder(state) {
      state.buyIntentionEdit = [];
      state.overviewEdit = [];
      state.offersList = [];
      state.idSelectedNow = undefined;
      state.totalPriceOffers = [];
      state.totalPriceOffersAux = [];
      state.totalPriceOffersOverview = [];
    },
    setIsProducerSearch(state, action: PayloadAction<boolean>) {
      state.isProducerSearch = action.payload;
    },
    setCurrentStep(state, action: PayloadAction<number>) {
      state.currentStep = action.payload;
    },
    setTotalPriceOverview(
      state,
      action: PayloadAction<BuyIntentionModels.SubTotalPrice>,
    ) {
      const { payload } = action;
      const subTotalFind = state.totalPriceOffersOverview?.filter(
        (product) => product.id === payload.id,
      ).length;
      if (subTotalFind > 0) {
        const updatedSubTotalList: any | undefined =
          state.totalPriceOffersOverview?.map((product: any) => {
            if (product.id === payload.id) {
              return payload;
            }
            return product;
          });
        state.totalPriceOffersOverview = updatedSubTotalList;
      } else {
        const addSubTotalList = [...state.totalPriceOffersOverview];
        addSubTotalList.push(payload);
        state.totalPriceOffersOverview = addSubTotalList;
      }
    },
    setTotalPriceOffers(
      state,
      action: PayloadAction<BuyIntentionModels.SubTotalPrice>,
    ) {
      const { payload } = action;
      const subTotalFind = state.totalPriceOffers?.filter(
        (product) => product.id === payload.id,
      ).length;
      if (subTotalFind > 0) {
        const updatedSubTotalList: any | undefined =
          state.totalPriceOffers?.map((product: any) => {
            if (product.id === payload.id) {
              return payload;
            }
            return product;
          });
        state.totalPriceOffers = updatedSubTotalList;
      } else {
        const addSubTotalList = [...state.totalPriceOffers];
        addSubTotalList.push(payload);
        state.totalPriceOffers = addSubTotalList;
      }
    },
    setTotalPriceOffersAux(
      state,
      action: PayloadAction<BuyIntentionModels.SubTotalPrice>,
    ) {
      const { payload } = action;
      const subTotalFind = state.totalPriceOffersAux?.filter(
        (product) => product.id === payload.id,
      ).length;
      if (subTotalFind > 0) {
        const updatedSubTotalList: any | undefined =
          state.totalPriceOffersAux?.map((product: any) => {
            if (product.id === payload.id) {
              return payload;
            }
            return product;
          });
        state.totalPriceOffersAux = updatedSubTotalList;
      } else {
        const addSubTotalList = [...state.totalPriceOffersAux];
        addSubTotalList.push(payload);
        state.totalPriceOffersAux = addSubTotalList;
      }
    },
    setDeleteTotalPriceOffers(
      state,
      action: PayloadAction<BuyIntentionModels.SubTotalPrice>,
    ) {
      const { payload } = action;
      const subTotalFind = state.totalPriceOffers?.filter(
        (product) => product.id !== payload.id,
      );
      state.totalPriceOffers = subTotalFind;
    },
    setSClearTotalPriceOffers: (state) => {
      state.totalPriceOffers = [];
    },
    setPagination: (
      state,
      action: PayloadAction<BuyIntentionModels.DefaultPagination>,
    ) => {
      state.pagination = action.payload;
    },
    setReloadPage: (state, action: PayloadAction<boolean>) => {
      state.reloadPage = action.payload;
    },
    setCustomer: (
      state,
      action: PayloadAction<BuyIntentionModels.Customer | undefined>,
    ) => {
      state.customer = action.payload;
    },
    setSubmitOffers: (state, action: PayloadAction<boolean>) => {
      state.submitOffers = action.payload;
    },
    setIdsProductsSelected: (state, action: PayloadAction<string>) => {
      const newIds = [...state.idsProductsSelected];
      if (newIds.indexOf(action.payload) === -1) {
        newIds.push(action.payload);
      }
      state.idsProductsSelected = newIds;
    },
    setIdsProductsAccepted: (state, action: PayloadAction<string>) => {
      const newIdsAccepted = [...state.idsProductsAccepted];
      if (newIdsAccepted.indexOf(action.payload) === -1) {
        newIdsAccepted.push(action.payload);
      }
      state.idsProductsAccepted = newIdsAccepted;
    },
    setDelIds: (state, action: PayloadAction<string>) => {
      const newIds = state.idsProductsSelected.filter(
        (product) => product !== action.payload,
      );
      const newIdsAccepted = state.idsProductsAccepted.filter(
        (product) => product !== action.payload,
      );
      state.idsProductsSelected = newIds;
      state.idsProductsAccepted = newIdsAccepted;
    },
    setClearIdsProduct: (state) => {
      state.idsProductsSelected = [];
      state.idsProductsAccepted = [];
      state.offersList = [];
    },
    setPropertyValue: (
      state,
      action: PayloadAction<BuyIntentionModels.SetPropertyAction>,
    ) => {
      const { payload } = action;
      const updatedTableData: any | undefined = state.buyIntentionEdit?.map(
        (order: any) => {
          if (order.id === payload.id) {
            const updatedData = order;
            updatedData[payload.property] = payload.value;
            return updatedData;
          }
          return order;
        },
      );
      state.buyIntentionEdit = updatedTableData;
    },
    setOverview: (
      state,
      action: PayloadAction<BuyIntentionModels.SetPropertyAction>,
    ) => {
      const { payload } = action;
      const updatedTableData: any | undefined = state.overviewEdit?.map(
        (order: any) => {
          if (order.id === payload.id) {
            const updatedData = order;
            updatedData[payload.property] = payload.value;
            return updatedData;
          }
          return order;
        },
      );
      state.overviewEdit = updatedTableData;
    },
    setDeleteOffer: (state, action: PayloadAction<string | undefined>) => {
      const { payload } = action;
      const newTableData: any[] | undefined = state.overviewEdit?.filter(
        (offer: any) => offer.id !== payload,
      );
      const newPrices = state.totalPriceOffersOverview?.filter(
        (price: any) => price.id !== payload,
      );
      const newPricesStepOne = state.totalPriceOffers?.filter(
        (price: any) => price.id !== payload,
      );

      const updatedTableData: any | undefined = state.buyIntentionEdit?.map(
        (order: any) => {
          if (order.id === payload) {
            const updatedData = order;
            updatedData.intention = false;
            updatedData.packingSelected = null;
            updatedData.unitPrice = 0;
            updatedData.quantityPacking = '';
            updatedData.quantityUnit = '';
            updatedData.unitPriceNumber = 0;
            updatedData.totalPrice = 0;
            updatedData.embPrice = 0;
            updatedData.observationText = '';
            updatedData.qualitySelect = '';
            updatedData.productPreference = undefined;
            updatedData.site = state.isProducerSearch ? updatedData.site : null;
            return updatedData;
          }
          return order;
        },
      );
      state.totalPriceOffersOverview = newPrices;
      state.totalPriceOffers = newPricesStepOne;
      state.buyIntentionEdit = updatedTableData;
      state.overviewEdit = newTableData;
    },
    setClearProducerOnTheTable: (state, action: PayloadAction<string | undefined>) => {
      const { payload } = action;
      const updatedTableData: any | undefined = state.buyIntentionEdit?.map(
        (order: any) => {
          if (order.id === payload) {
            const updatedData = order;
            updatedData.packingSelected = null;
            updatedData.unitPrice = 0;
            updatedData.quantityPacking = '';
            updatedData.quantityUnit = '';
            updatedData.unitPriceNumber = 0;
            updatedData.totalPrice = 0;
            updatedData.embPrice = 0;
            updatedData.observationText = '';
            updatedData.qualitySelect = '';
            updatedData.productPreference = undefined;
            updatedData.site = state.isProducerSearch ? updatedData.site : null;
            return updatedData;
          }
          return order;
        },
      );
      const updatedTableDataOverview: any | undefined = state.overviewEdit?.map(
        (order: any) => {
          if (order.id === payload) {
            const updatedData = order;
            updatedData.packingSelected = null;
            updatedData.unitPrice = 0;
            updatedData.quantityPacking = '';
            updatedData.quantityUnit = '';
            updatedData.unitPriceNumber = 0;
            updatedData.totalPrice = 0;
            updatedData.embPrice = 0;
            updatedData.observationText = '';
            updatedData.qualitySelect = '';
            updatedData.productPreference = undefined;
            updatedData.site = state.isProducerSearch ? updatedData.site : null;
            return updatedData;
          }
          return order;
        },
      );
      state.buyIntentionEdit = updatedTableData;
      state.overviewEdit = updatedTableDataOverview;
    },
    setOverviewEdit: (state) => {
      const addedProducts = [...state.overviewEdit];

      const addedIds: any = [];
      addedProducts.forEach((elemetn: any) => {
        addedIds.push(elemetn.id);
      });

      const addedPrices = state.totalPriceOffersAux?.filter(
        (product: BuyIntentionModels.SubTotalPrice) =>
          addedIds.includes(product.id),
      );

      state.totalPriceOffersOverview = addedPrices;
      state.overviewEdit = addedProducts;
    },
    setOverviewTable: (
      state,
      action: PayloadAction<BuyIntentionModels.BuyIntentionData>,
    ) => {
      const { payload } = action;
      const overviewNow = [...state.overviewEdit];
      overviewNow.push(payload);
      state.overviewEdit = overviewNow;
    },
    setOverviewItem: (
      state,
      action: PayloadAction<BuyIntentionModels.BuyIntentionData>,
    ) => {
      const { payload } = action;
      state.overviewEdit = state.overviewEdit.map((order: any) =>
        order.id === payload.id ? payload : order,
      );
    },
    setSubTotalList: (
      state,
      action: PayloadAction<BuyIntentionModels.SubTotalPrice>,
    ) => {
      const { payload } = action;
      const subTotalFind = state.subTotalPriceList?.filter(
        (product) => product.id === payload.id,
      ).length;
      if (subTotalFind > 0) {
        const updatedSubTotalList: any | undefined =
          state.subTotalPriceList?.map((product: any) => {
            if (product.id === payload.id) {
              return payload;
            }
            return product;
          });
        state.subTotalPriceList = updatedSubTotalList;
      } else {
        const addSubTotalList = [...state.subTotalPriceList];
        addSubTotalList.push(payload);
        state.subTotalPriceList = addSubTotalList;
      }
    },
    setSClearSubTotal: (state) => {
      state.subTotalPriceList = [];
    },
    setDatesForOffers: (
      state,
      action: PayloadAction<BuyIntentionModels.DatesForOffers>,
    ) => {
      state.deliveryDateForOffers = action.payload.delivery;
      state.comercialDateForOffers = action.payload.comercial;
    },
    setIdSelectedNow: (state, action: PayloadAction<string>) => {
      state.idSelectedNow = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setShowModalOffers: (state, action: PayloadAction<boolean>) => {
      state.showModalOffers = action.payload;
    },
    setResetValues: (state) => {
      state.buyIntentionEdit = [];
      state.overviewEdit = [];
      state.comercialDateForFinish = undefined;
      state.deliveryDateForFinish = undefined;
      state.idSelectedNow = undefined;
      state.totalPriceOffers = [];
    },
    setIdProductAdded: (state, action: PayloadAction<string | undefined>) => {
      state.idProductAdded = action.payload;
    },
    setReloadOffers: (state, action: PayloadAction<boolean>) => {
      state.reloadOffers = action.payload;
    },
    setDeliveryDateFinish: (state, action: PayloadAction<string>) => {
      state.deliveryDateForFinish = action.payload;
    },
    setComercialDateFinish: (state, action: PayloadAction<string>) => {
      state.comercialDateForFinish = action.payload;
    },
    setSubPriceAdded: (state, action: PayloadAction<number>) => {
      state.subPriceAdded = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(
      BuyIntentionCalls.fetchProductsList.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.productList = action.payload;
      },
    );
    builder.addCase(
      BuyIntentionCalls.fetchProducers.fulfilled,
      (state, action: PayloadAction<BuyIntentionModels.Producers[] | any>) => {
        state.producers = action.payload;
      },
    );
    builder.addCase(
      BuyIntentionCalls.fetchOffersByProducers.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.buyIntentionEdit = action.payload || [];
        state.isLoading = false;
        state.isProducerSearch = true;
      },
    );
    builder.addCase(
      BuyIntentionCalls.fetchOffersByProducts.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.buyIntentionEdit = action.payload || [];
        state.isLoading = false;
        state.isProducerSearch = false;
      },
    );
    builder.addCase(
      BuyIntentionCalls.fetchOffersList.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.offersList = action.payload;
        state.isLoading = false;
        state.reloadOffers = true;
      },
    );
    builder.addCase(
      BuyIntentionCalls.postBuyIntention.fulfilled,
      state => {
        state.isLoading = false;
        state.buyIntentionEdit = [];
        state.overviewEdit = [];
        state.comercialDateForFinish = undefined;
        state.deliveryDateForFinish = undefined;
        state.idSelectedNow = undefined;
        state.showIntentionModal = false;
        state.totalPriceOffers = [];
        state.currentStep = 0;
        state.totalPriceOffersOverview = [];
        state.totalPriceOffersAux = [];
      },
    );
    builder.addCase(
      BuyIntentionCalls.postBuyIntention.rejected,
      state => {
        state.isLoading = false;
      },
    );
    builder.addCase(
      BuyIntentionCalls.fetchPacking.fulfilled,
      (state, action) => {
        const updatedTableData: any | undefined = state.buyIntentionEdit?.map(
          (order: any) => {
            if (order.id === state.idSelectedNow) {
              const updatedData = order;
              updatedData.productPreference = action.payload;
              const packings = action.payload.filter(
                (packing: BuyIntentionModels.PackingFull) =>
                  packing.type === 'RETORNAVEL',
              );
              updatedData.packingSelected =
                packings.length > 0 ? packings[0] : action.payload[0];
              updatedData.quantityPacking =
                packings.length > 0 ? packings[0].qe : action.payload[0].qe;
              updatedData.quantityUnit =
                packings.length > 0 ? packings[0].qpe : action.payload[0].qpe;
              return updatedData;
            }
            return order;
          },
        );
        const updatedTableDataOverview: any | undefined = state.overviewEdit?.map(
          (order: any) => {
            if (order.id === state.idSelectedNow) {
              const updatedData = order;
              updatedData.productPreference = action.payload;
              const packings = action.payload.filter(
                (packing: BuyIntentionModels.PackingFull) =>
                  packing.type === 'RETORNAVEL',
              );
              updatedData.packingSelected =
                packings.length > 0 ? packings[0] : action.payload[0];
              updatedData.quantityPacking =
                packings.length > 0 ? packings[0].qe : action.payload[0].qe;
              updatedData.quantityUnit =
                packings.length > 0 ? packings[0].qpe : action.payload[0].qpe;
              return updatedData;
            }
            return order;
          },
        );
        state.buyIntentionEdit = updatedTableData;
        state.overviewEdit = updatedTableDataOverview;
        state.isLoading = false;
      },
    );
    builder.addCase(
      BuyIntentionCalls.fetchPacking.rejected,
      state => {
        state.isLoading = false;
      },
    );
  },
});

export const { actions, reducer } = buyIntentionSlice;
